import moment from "moment-timezone";

/**
 * Timestamp display, resolved against the viewer's own location.
 *
 * The API now emits every timestamp as ISO-8601 UTC, so the browser is free to
 * render it wherever the user actually is. Two rules keep that honest:
 *
 *  1. A value that arrives without an offset is treated as UTC, not as
 *     browser-local. Older endpoints still return naive `YYYY-MM-DD HH:mm:ss`
 *     strings, and `moment(value)` would read those as already-local — showing
 *     a UTC instant unconverted, which is exactly how the transaction detail
 *     page came to display two timezones side by side.
 *  2. Date-only values are never converted. Shifting `2025-11-06` into a zone
 *     behind UTC would roll it back to the 5th.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const OFFSETLESS_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

const FALLBACK_TIME_ZONE = "UTC";

let cachedZone = null;

/**
 * The viewer's IANA timezone, e.g. "Europe/London", "Africa/Accra", "Asia/Kolkata".
 *
 * Read from the browser's own locale settings, which already reflect where the
 * user is. The Geolocation API would answer the same question but costs a
 * permission prompt and a reverse-geocode, and is denied by default in most
 * corporate browser policies.
 */
export function userTimeZone() {
  if (cachedZone) return cachedZone;

  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    cachedZone = resolved && moment.tz.zone(resolved) ? resolved : FALLBACK_TIME_ZONE;
  } catch (e) {
    cachedZone = FALLBACK_TIME_ZONE;
  }

  return cachedZone;
}

/**
 * Parse a value of unknown provenance into a moment fixed to the correct
 * instant. Returns an invalid moment for empty or unparseable input so callers
 * can decide how to render a missing timestamp.
 */
export function parseAsUtc(value) {
  if (value === null || value === undefined || value === "") {
    return moment.invalid();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return moment.invalid();
    if (OFFSETLESS_DATETIME.test(trimmed)) return moment.utc(trimmed);
    return moment.utc(trimmed, moment.ISO_8601, false);
  }

  return moment.utc(value);
}

/**
 * Format a timestamp in the viewer's timezone.
 *
 * @param {*} value      timestamp from the API
 * @param {string} format moment format string
 * @param {string} placeholder rendered when the value is missing or unparseable
 */
export function formatInUserZone(value, format, placeholder = "--") {
  if (typeof value === "string" && DATE_ONLY.test(value.trim())) {
    return value.trim();
  }

  const parsed = parseAsUtc(value);
  if (!parsed.isValid()) return placeholder;

  return parsed.tz(userTimeZone()).format(format);
}

/**
 * Short zone label for the viewer's timezone, e.g. "GMT", "IST", "+04".
 * Shown alongside times so a bare clock reading can never be misread.
 */
export function userTimeZoneLabel(value) {
  const parsed = parseAsUtc(value);
  const at = parsed.isValid() ? parsed : moment.utc();
  return at.tz(userTimeZone()).format("z");
}
